import { MapPin, Tag, FileText, Layers, Hash, ArrowRight } from 'lucide-react';

function pathColor(sig) {
  const s = sig?.toLowerCase() || '';
  if (s.includes('pathogenic') && !s.includes('likely')) return { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' };
  if (s.includes('likely pathogenic')) return { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' };
  if (s.includes('likely benign')) return { bg: '#d1fae5', text: '#065f46', dot: '#10b981' };
  if (s.includes('benign')) return { bg: '#dcfce7', text: '#14532d', dot: '#22c55e' };
  return { bg: '#f1f5f9', text: '#475569', dot: '#94a3b8' };
}

function AnnotationChip({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs">
      {Icon && <Icon size={12} className="text-gray-400 shrink-0" />}
      <span className="text-gray-500">{label}:</span>
      <span className="font-mono font-medium text-gray-800">{value}</span>
    </div>
  );
}

function ScoreBadge({ label, value, bg, text }) {
  return (
    <div className="flex flex-col items-center px-6 py-4 rounded-xl border border-white/20 min-w-[120px]"
      style={{ background: 'rgba(255,255,255,0.08)' }}>
      <span className="text-white/60 text-xs uppercase tracking-widest mb-1">{label}</span>
      <span className="font-bold text-white text-lg leading-tight">{value}</span>
    </div>
  );
}

export default function VariantSummary({ variant }) {
  const pathC = pathColor(variant.clinvar.significance);
  const mgsColor = variant.mgs.score >= 0.8 ? '#f93441' : variant.mgs.score >= 0.5 ? '#f59e0b' : '#22c55e';

  return (
    <section id="summary" className="rounded-2xl overflow-hidden shadow-card">
      {/* Navy header */}
      <div className="px-6 pt-6 pb-5" style={{ background: '#021f30' }}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-extrabold font-mono text-white tracking-tight">
                {variant.id}
              </h2>
              {variant.rsid && (
                <span className="px-2.5 py-0.5 rounded-md text-xs font-mono font-semibold"
                  style={{ background: 'rgba(249,52,65,0.25)', color: '#ff6b75' }}>
                  {variant.rsid}
                </span>
              )}
              <span className="px-3 py-1 rounded-full text-sm font-bold text-white border border-white/30">
                {variant.gene}
              </span>
            </div>
            <p className="text-white/60 text-sm mt-1.5 font-mono">{variant.hgvsc} &nbsp;|&nbsp; {variant.hgvsp}</p>
          </div>

          {/* Three key badges */}
          <div className="flex gap-3 flex-wrap">
            <div className="flex flex-col items-center px-5 py-3 rounded-xl"
              style={{ background: pathC.bg, border: `1.5px solid ${pathC.dot}` }}>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: pathC.text }}>Pathogenicity</span>
              <span className="font-bold text-sm mt-0.5" style={{ color: pathC.text }}>{variant.clinvar.significance}</span>
            </div>

            <div className="flex flex-col items-center px-5 py-3 rounded-xl"
              style={{ background: 'rgba(249,52,65,0.15)', border: '1.5px solid rgba(249,52,65,0.4)' }}>
              <span className="text-xs font-semibold uppercase tracking-wide text-white/70">MGS Score</span>
              <span className="font-bold text-lg mt-0.5" style={{ color: mgsColor }}>
                {variant.mgs.score.toFixed(3)}
              </span>
            </div>

            <div className="flex flex-col items-center px-5 py-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.2)' }}>
              <span className="text-xs font-semibold uppercase tracking-wide text-white/70">ClinVar</span>
              <span className="font-bold text-sm mt-0.5" style={{ color: pathC.dot }}>{variant.acmg.classification}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Annotation chips */}
      <div className="px-6 py-4 bg-white border-t border-gray-100">
        <div className="flex flex-wrap gap-2">
          <AnnotationChip icon={MapPin} label="Chr" value={`chr${variant.chromosome}:${variant.position.toLocaleString()}`} />
          <AnnotationChip icon={ArrowRight} label="Ref/Alt" value={`${variant.ref} → ${variant.alt}`} />
          <AnnotationChip icon={Tag} label="Consequence" value={variant.consequence.replace(/_/g, ' ')} />
          <AnnotationChip icon={FileText} label="Transcript" value={variant.transcript} />
          <AnnotationChip icon={Layers} label="Exon" value={variant.exon} />
          <AnnotationChip icon={Hash} label="HGVS.c" value={variant.hgvsc} />
          <AnnotationChip icon={Hash} label="HGVS.p" value={variant.hgvsp} />
        </div>
      </div>
    </section>
  );
}
